import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import logo from "../../assets/logo.png";
import logoIcon from "../../assets/logo-icon.png";
import dashboardIcon from "../../assets/dashboard-icon.png";
import reportsIcon from "../../assets/reports-icon.png";
import downArrowIcon from "../../assets/down-arrow-icon.png";
import logoutIcon from "../../assets/logout-icon.png";
import './Sidebar.css';
import React, { useState } from 'react';
import SettingsModal from '../SettingsModal';
import { gsap } from 'gsap';
import Cookies from 'js-cookie';
import { LogOut, Languages } from 'lucide-react';

const translations = {
  en: {
    home: 'Home',
    scanAI: 'Scan AI',
    reports: 'Reports',
    settings: 'Settings',
    dutch: 'Dutch',
    logout: 'Logout'
  },
  nl: {
    home: 'Home',
    scanAI: 'Scan AI',
    reports: 'Rapporten',
    settings: 'Instellingen',
    dutch: 'Nederlands',
    logout: 'Uitloggen'
  }
};

const Sidebar = ({isDarkMode, setIsDarkMode, chats, setSelectedChat, isDutch, setIsDutch}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = translations[isDutch ? 'nl' : 'en'];

  const [showOptions, setShowOptions] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const toggleDutch = () => {
    const newValue = !isDutch;
    setIsDutch(newValue);
    Cookies.set('isDutch', String(newValue), { expires: 365 });
  };

  const handleLogout = () => {
    Cookies.remove('token')
    Cookies.remove('isDarkMode')
    Cookies.remove('email')
    Cookies.remove('userId')
    Cookies.remove('accountType')
    navigate('/login');
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    Cookies.set('isDarkMode', !isDarkMode)
  };

  const navLinks = useMemo(() => [
    {
      name: t.home,
      path: '/',
      icon: dashboardIcon
    },
    {
      name: t.scanAI,
      path: '/upload',
      icon: logoIcon
    },
    {
      name: t.reports,
      path: '/reports',
      icon: reportsIcon
    }
  ], [t.home, t.scanAI, t.reports]);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    // Helper functions for GSAP performance optimization
    const setGSAPProperties = (element, props) => {
      if (props.force3D) {
        element.style.transform = 'translate3d(0, 0, 0)';
      }
      if (props.willChange) {
        element.style.willChange = props.willChange;
      }
    };

    const resetGSAPProperties = (element) => {
      element.style.willChange = 'auto';
    };

    // Direct fadeUp animation
    const initFadeUp = () => {
      const fadeUpElements = document.querySelectorAll(".fadeUp");

      if (fadeUpElements.length === 0) {
        setTimeout(initFadeUp, 100);
        return;
      }

      // Set CSS properties directly for better performance
      fadeUpElements.forEach(element => {
        setGSAPProperties(element, { force3D: true, willChange: "transform, opacity" });
      });

      const animationDelay = 0; // Base delay in milliseconds

      gsap.set(fadeUpElements, { 
        opacity: 0, 
        y: 20
      });
      
      gsap.to(fadeUpElements, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        delay: 0.2 + (animationDelay / 1000), // Add navbar delay to base delay
        stagger: 0.1,
        ease: "power2.out",
        onComplete: () => {
          // Reset CSS properties after animation
          fadeUpElements.forEach(element => {
            resetGSAPProperties(element);
          });
        }
      });
    };

    initFadeUp();
}, [location]);


  return (
    <div className={`sidebar ${isDarkMode ? 'dark-mode' : ''}`}>
    <div className="sidebar-content">
      <div className="logo fadeUp"
      onClick={() => (navigate('/'))}>
        <img 
          src={logo} 
          alt="logo"
          className="logo-icon"
        />
      </div>

      <nav>
        <ul className="space-y-2">
          {navLinks.map((link) => (
            <li key={link.path} className='fadeUp' >
              <Link
                to={link.path}
                className={`nav-item flex items-center p-2 rounded-lg transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'active text-dark text-primary'
                    : ' '
                }`}
              >
                <img 
          src={link.icon} 
          alt="logo"
          className={`session-icon ${!isActive(link.path) && 'brightness-0'}`}
        />
                <span className={`${isActive(link.path) && 'text-primary font-bold'}`}>{link.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="bottom-section">
      {showOptions && (
          <div className="options text-left border rounded-md pt-2">
            <span className="text-left text-xs pl-4">{t.settings}</span>
            <div className='toggle-language options-item cursor-default flex items-center justify-between'>
                <span className='flex items-center gap-2'>
                <Languages className="size-4"/>
              <span className='text-sm'>{t.dutch}</span>
                </span>
                <input
                type="checkbox"
                checked={isDutch}
                onChange={toggleDutch}
                className="theme-checkbox toggle-english"
                />
            </div>
            <span className='options-item logout-option' onClick={handleLogout}>
                <LogOut className="size-4"/>
              <p className='text-sm'>{t.logout}</p>
            </span>
          </div>
        )}
        <div className={`border user-profile ${showOptions && "active"} fadeUp`} onClick={toggleOptions}>
          <span className='truncate'>
            {/* <img 
              src={profileIcon} 
              alt="logo"
              className="w-10"/> */}
            <p className="user-name truncate">{Cookies.get('email')}</p>
          </span>
          <img 
            src={downArrowIcon} 
            alt="down arrow"
            className="down-arrow-icon"
          />
        </div>
        
      </div>
      
      </div>

      <SettingsModal 
        isOpen={isModalOpen} 
        onClose={toggleModal} 
        onToggleDarkMode={toggleDarkMode} 
        isDarkMode={isDarkMode} 
      />
    </div>
  );
};

export default Sidebar;